import {useEffect} from 'react';
import {useExperiments, WidgetProps} from '@wix/yoshi-flow-editor';
import type {CheckoutBrand} from '@wix/ambassador-ecom-v1-ecommerce-settings/types';
import {
  BM_ORIGIN,
  MessageSource,
  MessageType,
  type DesignChangeMessage,
  type ReadyMessage,
} from '../../../domain/composer/messages';
import {CheckoutControllerProps} from '../../../types/checkoutApp.types';
import {SPECS} from '../../../common/constants';

export type AttachComposerListenerParams = {
  onBrandOverride: (brand: CheckoutBrand) => void;
  win?: Window;
};

export function attachComposerListener({onBrandOverride, win}: AttachComposerListenerParams): () => void {
  const targetWindow = win ?? window;

  const handler = (event: MessageEvent): void => {
    if (event.origin !== BM_ORIGIN) {
      return;
    }
    const data = event.data as Partial<DesignChangeMessage>;
    if (data?.source !== MessageSource.Composer) {
      return;
    }
    if (data.type === MessageType.DesignChange && data.payload?.brand) {
      onBrandOverride(data.payload.brand);
    }
  };

  targetWindow.addEventListener('message', handler);

  const ready: ReadyMessage = {source: MessageSource.Viewer, type: MessageType.Ready};
  targetWindow.parent?.postMessage(ready, BM_ORIGIN);

  return () => targetWindow.removeEventListener('message', handler);
}

export const useComposerListener = (controllerProps: WidgetProps<CheckoutControllerProps>): void => {
  const {experiments} = useExperiments();
  const isBMPreview = controllerProps?.navigationStore?.isBMPreview ?? false;
  const onBrandOverride = controllerProps?.checkoutSettingsStore?.onBrandOverride;
  const enabled = isBMPreview && experiments.enabled(SPECS.ecomCheckoutComposerViewer);

  useEffect(() => {
    if (!enabled || !onBrandOverride) {
      return;
    }
    return attachComposerListener({onBrandOverride});
  }, [enabled, onBrandOverride]);
};
